/**
 * <lookbook-showcase> — the homepage's lookbook tabs (SAVETTE-49).
 *
 * The markup arrives from Liquid as a complete WAI-ARIA tabs widget: a tablist of buttons, one
 * tabpanel per lookbook holding its two images and its "View the lookbook" link, the first tab
 * selected and every other panel hidden. This element makes it work:
 *
 * - a click or tap selects a tab (on a phone that is the only way, as the design has it);
 * - at the desktop layout, moving a mouse over a tab selects it too, so its images and its link
 *   appear as the pointer passes over, which is Kylie's brief; leaving the row keeps the last one
 *   shown. It takes a real movement: the page scrolling under a resting cursor changes nothing;
 * - the arrow keys move between tabs and select as they go (automatic activation), Home and End
 *   jump to the ends, and only the selected tab sits in the tab order, so Tab goes from it straight
 *   to its panel's link. Up and Down are the natural keys on desktop, where the tabs are a column,
 *   and Left and Right on a phone, where they are a row; both pairs work at every width and
 *   aria-orientation follows the layout.
 *
 * The images of the panels not yet shown are lazy, so they would only start loading at the moment
 * a shopper asked for them. At the desktop layout, where hovering swaps them, they are told to load
 * once the module comes near the screen, so the swap is instant. A phone only swaps on a tap, so it
 * downloads nothing it has not been asked for.
 *
 * The module is marked once the shopper picks a lookbook, and only then do newly shown images fade
 * in: the first lookbook's images are never held back by a fade on arrival.
 */
const WIDE = '(min-width: 1025px)';

class LookbookShowcase extends HTMLElement {
  connectedCallback() {
    this.tablist = this.querySelector('[role="tablist"]');
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
    if (!this.tablist || this.tabs.length === 0 || this.panels.includes(null)) return;

    this.wide = window.matchMedia(WIDE);
    this.wide.addEventListener('change', this.orient);
    this.orient();

    this.tablist.addEventListener('click', this.onClick);
    this.tablist.addEventListener('keydown', this.onKeydown);
    this.tablist.addEventListener('pointermove', this.onPointerMove);
    this.tablist.addEventListener('pointerleave', this.onPointerLeave);
    // the theme editor selects a tab when the merchant picks its block in the sidebar
    this.addEventListener('shopify:block:select', this.onBlockSelect);

    this.warmer = new IntersectionObserver(this.onNear, { rootMargin: '300px 0px' });
    this.warmer.observe(this);
  }

  disconnectedCallback() {
    this.wide?.removeEventListener('change', this.orient);
    this.tablist?.removeEventListener('click', this.onClick);
    this.tablist?.removeEventListener('keydown', this.onKeydown);
    this.tablist?.removeEventListener('pointermove', this.onPointerMove);
    this.tablist?.removeEventListener('pointerleave', this.onPointerLeave);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.warmer?.disconnect();
  }

  get index() {
    return Math.max(
      0,
      this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'),
    );
  }

  /** A column of tabs on desktop, a row on a phone. */
  orient = () => {
    this.tablist.setAttribute('aria-orientation', this.wide.matches ? 'vertical' : 'horizontal');
  };

  onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;
    this.select(this.tabs.indexOf(tab));
  };

  /**
   * Hover selects, but only at the desktop layout, only for a mouse or a pen (a tap also reports a
   * pointer over the tab, and the click that follows it is what selects on a touch screen), and
   * only when the pointer actually moved: a browser may report a pointer event with no movement
   * when the page scrolls under a resting cursor, and that must not switch the lookbook. Movement is
   * read from the pointer's own last position over the tabs, which every browser reports alike; the
   * first event after the pointer arrives only records where it is.
   */
  onPointerMove = (event) => {
    if (!this.wide.matches) return;
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    const last = this.lastPoint;
    this.lastPoint = { x: event.clientX, y: event.clientY };
    if (!last || (last.x === event.clientX && last.y === event.clientY)) return;
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;
    const index = this.tabs.indexOf(tab);
    if (index !== this.index) this.select(index);
  };

  onPointerLeave = () => {
    this.lastPoint = null;
  };

  onKeydown = (event) => {
    // a modified arrow is the browser's or the system's (Alt+Left is Back), never the tabs'
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const i = this.tabs.indexOf(event.target.closest('[role="tab"]'));
    if (i === -1) return;
    const last = this.tabs.length - 1;
    const keys = {
      ArrowDown: i + 1,
      ArrowRight: i + 1,
      ArrowUp: i - 1,
      ArrowLeft: i - 1,
      Home: 0,
      End: last,
    };
    if (!(event.key in keys)) return;
    event.preventDefault();
    // the tabs wrap at both ends, as the tabs pattern expects
    const count = this.tabs.length;
    this.select((keys[event.key] + count) % count, { focus: true });
  };

  onBlockSelect = (event) => {
    const index = this.tabs.findIndex((tab) => tab.contains(event.target));
    if (index !== -1) this.select(index);
  };

  select(next, { focus = false } = {}) {
    const index = Math.min(Math.max(next, 0), this.tabs.length - 1);
    if (index === this.index) {
      // nothing changes, and marking the module now would fade the images already showing
      if (focus) this.tabs[index].focus();
      return;
    }
    this.dataset.picked = '';
    this.tabs.forEach((tab, i) => {
      const current = i === index;
      tab.setAttribute('aria-selected', current ? 'true' : 'false');
      tab.tabIndex = current ? 0 : -1;
    });
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
    if (focus) this.tabs[index].focus();
  }

  /**
   * Near the screen at the desktop layout: let every panel's images load, so a hover swaps without
   * a wait. Narrower, keep watching; a window widened later warms them the next time it nears.
   */
  onNear = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    if (!this.wide.matches) return;
    this.warmer.disconnect();
    this.panels.forEach((panel) => {
      panel.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    });
  };
}

if (!customElements.get('lookbook-showcase')) customElements.define('lookbook-showcase', LookbookShowcase);
